import { FC, useState, useEffect, useCallback } from 'react';
import { Setting } from '../../types/constant/PagesPropsType';
import couponClass from '../../assets/scss/SubscriptionDetail/Coupon.module.scss';
import {
  SubscriptionApiType,
  SubscriptionDiscountsApiType,
} from '../../types/api/SubscriptionApiType';
import couponCheck from '../../assets/images/coupon_check.png';
import { useMasterContext } from '../../context';
import { postMessageGtag } from '../../utils/trackClick';
import { DistributeCoupon } from '../../api/graphql/master/types';
import {
  calculateCouponDiscountBreakdown,
  calculateDisplayShippingPrice,
} from '../../utils/subscriptionDetail/couponAndShippingEstimate';

type ThanksmileCoupon = {
  title: string;
  code: string;
};

export const Coupon: FC<{
  loading: boolean;
  coupon: string;
  subscription: SubscriptionApiType;
  setting: Setting;
  clickUpdateBtnFunc: () => void;
  changeValueFunc: (value: string) => void;
  thanksmileCoupons: ThanksmileCoupon[];
  isAnchorLink: boolean;
}> = ({
  loading,
  coupon,
  subscription,
  setting,
  clickUpdateBtnFunc,
  changeValueFunc,
  thanksmileCoupons,
  isAnchorLink,
}) => {
  const { settings, distributeCoupons } = useMasterContext();

  useEffect(() => {
    if (!isAnchorLink) return;

    setTimeout(() => {
      anchorLinkCoupon();
    }, 200);
  }, [isAnchorLink]);

  const clickUpdateBtnHandler = useCallback(() => {
    if (loading) return;
    clickUpdateBtnFunc();
    postMessageGtag('click_ApplyCoupon', 'detailCoupon');
  }, [clickUpdateBtnFunc, loading]);

  const changeValueHandler = useCallback(
    (e) => {
      changeValueFunc(e.target.value);
      e.target.reportValidity();
    },
    [changeValueFunc]
  );

  const [subtotalPrice, setSubtotalPrice] = useState(0);
  const [discountCoupon, setDiscountCoupon] = useState<SubscriptionDiscountsApiType[]>([]);
  const [isInputDisable, setIsInputDisable] = useState(false);
  const [applyButtonHtml, setApplyButtonHtml] = useState<JSX.Element>(<></>);
  const [checkmark, setCheckmark] = useState<JSX.Element>(<></>);
  const [couponDiscountPrice, setCouponDiscountPrice] = useState(0);
  const [couponDiscountPriceHtml, setCouponDiscountPriceHtml] = useState<JSX.Element>(<></>);
  const [attensionMainHtml, setAttensionMainHtml] = useState<JSX.Element>(<></>);
  const [attensionSubHtml, setAttensionSubHtml] = useState<JSX.Element>(<></>);
  const [isOpen, setIsOpen] = useState(false);
  const [couponApplyStatus, setCouponApplyStatus] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState<DistributeCoupon[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<DistributeCoupon | null>(null);
  const [couponAttention, setCouponAttention] = useState(<></>);
  const [isDisplayAllAvailableCoupons, setIsDisplayAllAvailableCoupons] = useState(true);
  const shippingPrice = subscription ? calculateDisplayShippingPrice(subscription) : 0;

  useEffect(() => {
    if (isAnchorLink && !isOpen) {
      setIsOpen(isAnchorLink);
    }
  }, [isAnchorLink, isOpen]);

  useEffect(() => {
    // アコーディオンが閉じた状態から開く場合のみ高さ調整
    const height = document.querySelector('body')!.scrollHeight;
    setting.resize(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!subscription) return;
    // 小計（定期便に登録されている商品の金額×数量）
    setSubtotalPrice(
      subscription.subscriptionLines.reduce((total, line) => {
        return total + line.currentPriceAmount * line.quantity;
      }, 0)
    );
    // クーポン情報
    setDiscountCoupon(
      subscription.subscriptionDiscounts.filter((discount) => {
        return discount.coupon === true;
      })
    );
  }, [subscription]);

  useEffect(() => {
    if (!subscription) return;

    let applyStatus = ''; //クーポン割引き表示状態('':表示なし applying:適用中表示 apply:割引表示)
    if (subscription.isSyncingSubscription) {
      // クーポンを適用して、Shopifyとの割引含む契約情報の同期が完了していない状態
      applyStatus = 'applying';
    } else if (discountCoupon.length !== 0) {
      const actualAmountNull = discountCoupon.filter((coupon) => coupon.actualAmount === null);
      if (actualAmountNull.length > 0) {
        if (subscription.startSyncSubscriptionDate === null) {
          // ディスカウントアップデートリリース前にクーポンが適用されて、一度もShopifyとの同期処理が走っていない状態
          applyStatus = 'applying';
        }
      } else {
        // クーポン適用、Shopify同期完了
        applyStatus = 'apply';
      }
    }
    setCouponApplyStatus(applyStatus);
  }, [discountCoupon, subscription]);

  useEffect(() => {
    if (!subscription) return;
    // 使用可能クーポンの取得
    const metafieldCoupons = matchMetafieldCoupons();
    const exChangedCoupons = formatThanksmileCoupons(thanksmileCoupons);
    const coupons = metafieldCoupons.concat(exChangedCoupons);
    if (coupons.length !== 0) {
      setAvailableCoupons(coupons);
      setIsOpen(true);
      if (coupons.length > 3) {
        setIsDisplayAllAvailableCoupons(false);
      }
    }

    function matchMetafieldCoupons() {
      if (distributeCoupons.length !== 0) {
        const billingAttempts = subscription.billingAttempts.filter(
          (attempt) => !attempt.errorCode && attempt.errorMessage === ''
        );
        const totalOrderCount = billingAttempts.length + 1; //初回決済を含む
        const countMatchCoupons = distributeCoupons.filter(
          (item) => item.count === totalOrderCount
        );
        // 最新の決済日を取得(初回決済のみは契約作成日、それ以外は決済エラーを除いた最上位の決済日)
        const createAt =
          totalOrderCount === 1 ? subscription.createdAt.toString() : billingAttempts[0].createdAt;

        if (countMatchCoupons.length !== 0 && createAt) {
          const createAtUtc = new Date(createAt.slice(0, 19));
          const jstDate = new Date(createAtUtc.getTime() + 9 * 3600 * 1000); //ミリ秒単位で加算

          const coupons = countMatchCoupons.filter((coupon) => {
            if (coupon.from && coupon.until) {
              return new Date(coupon.from) <= jstDate && jstDate <= new Date(coupon.until);
            } else if (!coupon.from && !coupon.until) {
              return true;
            } else if (coupon.from) {
              return new Date(coupon.from) <= jstDate;
            } else if (coupon.until) {
              return jstDate <= new Date(coupon.until);
            } else {
              return false;
            }
          });

          return coupons;
        }
      }
      return [];
    }

    function formatThanksmileCoupons(thanksmileCoupons: ThanksmileCoupon[]): DistributeCoupon[] {
      if (thanksmileCoupons.length !== 0) {
        const coupons = thanksmileCoupons.map((coupon) => {
          return {
            count: 0,
            title: coupon.title,
            coupon: coupon.code,
          };
        });
        return coupons;
      } else {
        return [];
      }
    }
  }, [distributeCoupons, subscription, thanksmileCoupons]);

  useEffect(() => {
    let attention = '';
    if (discountCoupon.length !== 0) {
      const isSyncing = subscription?.isSyncingSubscription ?? false;
      // 説明文の表示可否は、同期中/同期完了後で判定条件を切り替える。
      // （金額表示側の意図と揃える）
      const canShowAppliedCoupon = isSyncing
        ? couponApplyStatus === 'applying' || couponApplyStatus === 'apply'
        : couponApplyStatus === 'apply';

      if (canShowAppliedCoupon) {
        const breakdown = calculateCouponDiscountBreakdown(subscription, subtotalPrice, shippingPrice);
        const hasShippingCoupon = discountCoupon.some(
          (discount) => discount.targetType === 'SHIPPING_LINE'
        );

        if (hasShippingCoupon) {
          // 送料値引きが実際に発生しているときだけ文言を表示
          if (breakdown.shippingLine > 0) {
            attention = '送料無料クーポン適用中';
          }
        } else if (breakdown.lineItem > 0) {
          // 商品対象クーポンは lineItem の計算結果を表示に使う
          attention = breakdown.lineItem.toLocaleString() + '円OFFクーポン適用中';
        }
      }
    } else if (selectedCoupon) {
      if (coupon === '') {
        setSelectedCoupon(null);
        setCouponAttention(<></>);
      } else {
        attention = 'クーポンを使用するには適用を押してください。';
      }
    }

    if (attention !== '') {
      setCouponAttention(<div className={couponClass.selected}>{attention}</div>);
    } else {
      setCouponAttention(<></>);
    }
  }, [
    coupon,
    couponApplyStatus,
    discountCoupon,
    selectedCoupon,
    subscription,
    subtotalPrice,
    shippingPrice,
  ]);

  useEffect(() => {
    if (discountCoupon.length > 0) {
      // アコーディオン
      setIsOpen(true);
      // クーポン名入力欄非活性
      setIsInputDisable(true);
      // チェックマーク
      setCheckmark(
        <img
          className={couponClass.checkmark}
          src={couponCheck}
          alt=""
        />
      );

      // クーポン割引金額合計
      setCouponDiscountPrice(
        calculateCouponDiscountBreakdown(subscription, subtotalPrice, shippingPrice).total
      );
    } else {
      // クーポン未適用時は値引き額を明示的に 0 へ戻す
      setCouponDiscountPrice(0);
    }
  }, [
    discountCoupon,
    subscription,
    subtotalPrice,
    shippingPrice,
  ]);

  useEffect(() => {
    if (discountCoupon.length > 0) {
      // 適用ボタン
      setApplyButtonHtml(<button disabled>適用済み</button>);
    } else {
      setApplyButtonHtml(<button onClick={clickUpdateBtnHandler}>適用</button>);
    }
  }, [clickUpdateBtnHandler, discountCoupon]);

  useEffect(() => {
    if (discountCoupon.length > 0 && couponApplyStatus !== '' && couponDiscountPrice > 0) {
      // 値引き額の表示
      setCouponDiscountPriceHtml(
        <dl className={`${couponClass.coupon__dl} ${couponClass.couponBorder}`}>
          <dt>
            <p className={couponClass.text}>クーポン値引き：</p>
          </dt>
          <dd className={couponClass.coupon__dd}>
            <>
              <span className={`${couponClass.price} ${couponClass.redText}`}>-</span>
              <span className={`${couponClass.yenSign} ${couponClass.redText}`}>¥</span>
              <span className={`${couponClass.price} ${couponClass.redText}`}>
                {couponDiscountPrice.toLocaleString()}
              </span>
            </>
          </dd>
        </dl>
      );
    } else {
      // 値引きが 0 円のときは行自体を表示しない
      setCouponDiscountPriceHtml(<></>);
    }
  }, [discountCoupon, couponDiscountPrice, couponApplyStatus]);

  useEffect(() => {
    if (!settings) return;
    const shippingFeeAttension = settings['shippingFeeAttension'];
    // 注釈本文
    let isDisplayMain = false;
    if (shippingFeeAttension['main']) {
      if (subscription && !subscription.excludeFromAutoCalculateDeliveryPrice) {
        // 送料無料クーポン適用
        const shippingCoupon = subscription.subscriptionDiscounts.some(
          (discount) => discount.coupon === true && discount.targetType === 'SHIPPING_LINE'
        );

        if (shippingCoupon) {
          if (shippingFeeAttension['shippingCouponHtml']) {
            setAttensionMainHtml(
              <div
                className={couponClass.attensionMain}
                dangerouslySetInnerHTML={{
                  __html: shippingFeeAttension['shippingCouponHtml'],
                }}
              />
            );
            isDisplayMain = true;
          }
        } else {
          setAttensionMainHtml(
            <div className={couponClass.attensionMain}>{shippingFeeAttension['main']}</div>
          );
          isDisplayMain = true;
        }
      }
    }
    // 注釈補足
    if (shippingFeeAttension['sub']) {
      // mainの有無でマージンを調整
      let interval = couponClass.attensionLargeInterval;
      if (isDisplayMain) {
        interval = couponClass.attensionSmallInterval;
      }
      setAttensionSubHtml(
        <div className={`${couponClass.attensionSub} ${interval}`}>
          {shippingFeeAttension['sub']}
        </div>
      );
    }
  }, [settings, subscription]);

  const toggleClass = () => {
    // クーポン適用済みの場合はアコーディオンは開いたまま
    if (discountCoupon.length > 0) return;
    setIsOpen(!isOpen);
  };

  const onClickAvailableCoupon = useCallback(
    (index: number) => {
      if (discountCoupon.length !== 0) return;
      const selectCoupon = availableCoupons[index];
      setSelectedCoupon(selectCoupon);
      changeValueFunc(selectCoupon.coupon);

      // スクロール
      setTimeout(() => {
        anchorLinkCoupon();
      }, 100);
    },
    [availableCoupons, changeValueFunc, discountCoupon]
  );

  // 小計金額をGTAGで送る
  useEffect(() => {
    if (subtotalPrice <= 0) return;

    postMessageGtag('cart_subtotal_amount', 'detailSettings', subtotalPrice);
  }, [subtotalPrice]);

  const displayAvailableCoupons = isDisplayAllAvailableCoupons
    ? availableCoupons
    : availableCoupons.slice(0, 3);

  return (
    <div className={couponClass.coupon}>
      <div className={couponClass.useCoupon}>
        <button
          className={couponClass.title}
          onClick={() => {
            toggleClass();
          }}
        >
          クーポンを使う
          <span className={`${isOpen ? couponClass.angleUp : couponClass.angleDown}`}></span>
        </button>
        <div className={`${isOpen ? couponClass.open : couponClass.closed}`}>
          <div className={couponClass.couponForm}>
            <form>
              <input
                value={coupon}
                onChange={changeValueHandler}
                required
                disabled={isInputDisable}
                onClick={() => {
                  postMessageGtag('click_CouponCode', 'detailCoupon');
                }}
              />
              {checkmark}
            </form>
            {applyButtonHtml}
          </div>
          {couponAttention}
          <p className={couponClass.couponNote}>・クーポン適用後は変更できません。</p>
          {availableCoupons.length !== 0 && (
            <div className={couponClass.distributeCouponsContainer}>
              <div className={couponClass.distributeCouponsCountContainer}>
                <div>使用できるクーポン</div>
                <div>
                  <span className={couponClass.distributeCouponsCount}>
                    {availableCoupons.length}
                  </span>
                  件
                </div>
              </div>
              {displayAvailableCoupons.map((c, index) => (
                <div
                  className={couponClass.distributeCouponItemContainer}
                  style={{ opacity: discountCoupon.length === 0 ? 'inherit' : '0.6' }}
                  key={index}
                >
                  <div className={couponClass.distributeCouponItemText}>
                    {c.expiry && (
                      <>
                        <span className={couponClass.distributeCouponItemExpiry}>{c.expiry}</span>
                        <br />
                      </>
                    )}
                    <span className={couponClass.distributeCouponItemTitle}>{c.title}</span>
                    <br />
                    {c.coupon}
                  </div>
                  {c.coupon === coupon ? (
                    <button disabled>選択済み</button>
                  ) : (
                    <button
                      className={couponClass.selectBtn}
                      onClick={() => onClickAvailableCoupon(index)}
                      disabled={discountCoupon.length !== 0}
                    >
                      選択
                    </button>
                  )}
                </div>
              ))}
              {!isDisplayAllAvailableCoupons && (
                <div className={couponClass.seeMoreLinkContainer}>
                  <span
                    className={couponClass.seeMoreLink}
                    onClick={() => setIsDisplayAllAvailableCoupons(true)}
                  >
                    クーポンをもっと見る
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        <dl className={couponClass.coupon__dl}>
          <dt>
            <p className={couponClass.text}>小計：</p>
          </dt>
          <dd className={couponClass.coupon__dd}>
            <span className={couponClass.yenSign}>¥</span>
            <span className={couponClass.price}>{subtotalPrice.toLocaleString()}</span>
          </dd>
        </dl>
        <dl className={couponClass.coupon__dl}>
          <dt>
            <p className={couponClass.text}>配送料：</p>
          </dt>
          <dd className={couponClass.coupon__dd}>
            <>
              <span className={couponClass.yenSign}>¥</span>
              <span className={couponClass.price}>{shippingPrice.toLocaleString()}</span>
            </>
          </dd>
        </dl>
        {couponDiscountPriceHtml}
        <div className={couponClass.total}>
          <dl className={couponClass.total__dl}>
            <dt>
              <p className={couponClass.text}>支払合計：</p>
            </dt>
            <dd className={couponClass.coupon__dd}>
              <>
                <span className={couponClass.yenSign}>¥</span>
                <span className={couponClass.totalPrice}>
                  {Math.max(0, subtotalPrice + shippingPrice - couponDiscountPrice).toLocaleString()}
                </span>
              </>
            </dd>
          </dl>
        </div>
        <div className={couponClass.taxText}>
          <span>価格はすべて税込表示</span>
        </div>
      </div>
      {attensionMainHtml}
      {attensionSubHtml}
    </div>
  );
};

function anchorLinkCoupon() {
  const couponElement = document.getElementById('coupon-section');
  if (couponElement) {
    const targetRect = couponElement.getBoundingClientRect();
    window.parent.postMessage(
      {
        action: 'scrollToCoupon',
        top: targetRect.top,
      },
      '*'
    );
  }
}
